import type { IFileAttachment } from '../types';

export interface FileConversionResult {
  name: string;
  dataUri: string;
  mimeType: string;
  sizeBytes: number;
}

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB for images
const MAX_DOCUMENT_SIZE = 20 * 1024 * 1024; // 20MB for documents
export const MAX_FILE_COUNT = 3; // Total attachments (images + documents)
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

// Note: Office documents (docx, pptx, xlsx) are NOT supported by Azure Responses API.
// They cannot be sent via CreateInputFilePart and require special parsing.
// Only PDF and text-based formats are supported.
const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'text/plain',
  'text/markdown',
  'application/json',
  'text/html',
  'application/xml',
  'text/xml',

  // Office documents
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
  'application/vnd.ms-excel', // XLS
  'application/vnd.ms-powerpoint' // PPT
];

// Extension to MIME type mapping for files where browser doesn't provide correct MIME type
const EXTENSION_TO_MIME: Record<string, string> = {
  '.md': 'text/markdown',
  '.markdown': 'text/markdown',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.json': 'application/json',
  '.html': 'text/html',
  '.htm': 'text/html',
  '.xml': 'application/xml',
  '.pdf': 'application/pdf',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls': 'application/vnd.ms-excel',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.ppt': 'application/vnd.ms-powerpoint',
};

/**
 * Get the effective MIME type for a file, falling back to extension-based detection.
 * Browsers often report empty or generic MIME types for certain file extensions.
 *
 * @param file - File to get MIME type for
 * @returns The MIME type string
 */
export function getEffectiveMimeType(file: File): string {
  // If browser provides a specific MIME type, use it
  if (file.type && file.type !== 'application/octet-stream') {
    return file.type.toLowerCase();
  }

  // Fall back to extension-based detection
  const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] || '';
  return EXTENSION_TO_MIME[extension] || file.type || '';
}

/**
 * Validate if a file is a supported image type and within size limits.
 *
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 */
export function validateImageFile(file: File): FileValidationResult {
  const mimeType = getEffectiveMimeType(file);

  if (!mimeType.startsWith('image/')) {
    return { valid: false, error: `"${file.name}" is not an image file` };
  }

  if (!ALLOWED_IMAGE_TYPES.includes(mimeType)) {
    return { valid: false, error: `"${file.name}" format not supported. Use PNG, JPEG, GIF, or WebP` };
  }

  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return { valid: false, error: `"${file.name}" is ${sizeMB}MB. Maximum file size is 5MB` };
  }

  return { valid: true };
}

/**
 * Validate if a file is a supported document type and within size limits.
 *
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 */
export function validateDocumentFile(file: File): FileValidationResult {
  const mimeType = getEffectiveMimeType(file);

  if (!ALLOWED_DOCUMENT_TYPES.includes(mimeType)) {
    return {
      valid: false,
      error: `"${file.name}" format not supported. Use PDF, TXT, MD, CSV, JSON, HTML, or XML`
    };
  }

  if (file.size > MAX_DOCUMENT_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return { valid: false, error: `"${file.name}" is ${sizeMB}MB. Maximum file size is 20MB` };
  }

  return { valid: true };
}

/**
 * Validate any file (image or document) based on its type.
 *
 * @param file - File to validate
 * @returns Validation result with error message if invalid
 */
export function validateFile(file: File): FileValidationResult {
  const mimeType = getEffectiveMimeType(file);

  if (mimeType.startsWith('image/')) {
    return validateImageFile(file);
  } else if (ALLOWED_DOCUMENT_TYPES.includes(mimeType)) {
    return validateDocumentFile(file);
  } else {
    return {
      valid: false,
      error: `"${file.name}" is not a supported file type`
    };
  }
}

/**
 * Validate multiple files for count and individual file requirements.
 *
 * @param files - Files to validate
 * @param currentFileCount - Number of files already attached
 * @returns Validation result with error message if invalid
 */
export function validateFileCount(files: File[], currentFileCount: number = 0): FileValidationResult {
  const totalCount = currentFileCount + files.length;

  if (totalCount > MAX_FILE_COUNT) {
    return {
      valid: false,
      error: `Maximum ${MAX_FILE_COUNT} files allowed. You have ${currentFileCount} attached and are trying to add ${files.length} more`
    };
  }

  return { valid: true };
}

/**
 * Convert a single file to base64 data URI with correct MIME type.
 * The browser's FileReader may embed incorrect MIME types, so we rebuild
 * the data URI with the effective MIME type.
 *
 * @param file - File to convert
 * @param effectiveMimeType - The correct MIME type to use
 * @returns Promise resolving to data URI string with correct MIME type
 * @throws {Error} If file reading fails
 */
async function convertFileToDataUri(file: File, effectiveMimeType: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // The data URI format is: data:[<mediatype>][;base64],<data>
      // We need to replace the MIME type portion with the correct one
      const commaIndex = result.indexOf(',');
      if (commaIndex === -1) {
        reject(new Error('Invalid data URI format'));
        return;
      }
      const base64Data = result.substring(commaIndex + 1);
      const correctedDataUri = `data:${effectiveMimeType};base64,${base64Data}`;
      resolve(correctedDataUri);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Convert multiple files to base64 data URIs with metadata.
 * Supports both images and documents.
 *
 * @param files - Array of File objects to convert
 * @returns Array of conversion results with file metadata
 * @throws {Error} If any file is invalid or conversion fails
 */
export async function convertFilesToDataUris(
  files: File[]
): Promise<FileConversionResult[]> {
  const results: FileConversionResult[] = [];

  for (const file of files) {
    // Validate each file before conversion
    const validation = validateFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    const effectiveMimeType = getEffectiveMimeType(file);
    const dataUri = await convertFileToDataUri(file, effectiveMimeType);

    results.push({
      name: file.name,
      dataUri,
      mimeType: effectiveMimeType,
      sizeBytes: file.size,
    });
  }

  return results;
}

/**
 * Create chat attachment metadata from file conversion results.
 *
 * @param results - File conversion results
 * @returns Array of attachment objects for chat UI
 */
export function createAttachmentMetadata(
  results: FileConversionResult[]
): IFileAttachment[] {
  return results.map((result) => ({
    fileName: result.name,
    fileSizeBytes: result.sizeBytes,
    dataUri: result.dataUri,
  }));
}

/**
 * Convierte los attachments recibidos desde Microsoft Teams
 * en archivos listos para enviar al backend (base64 + metadata).
 */
export async function convertTeamsAttachments(
  attachments: any[],
  httpClient?: AuthenticatedHttpClient
): Promise<FileConversionResult[]> {

  // Lista donde se guardarán los archivos ya convertidos
  const results: FileConversionResult[] = [];

  // Recorremos todos los attachments que vienen del mensaje de Teams
  for (const attachment of attachments) {

    // Validamos que el attachment sea un archivo descargable de Teams.
    // Dos formas: archivo subido por el clip (SharePoint, content.downloadUrl),
    // o imagen adjuntada/pegada directamente (inline, contentUrl).
    const isFileDownloadInfo = attachment?.contentType === "application/vnd.microsoft.teams.file.download.info" &&
      attachment?.content?.downloadUrl;
    const isInlineImage = typeof attachment?.contentType === "string" &&
      attachment.contentType.startsWith("image/") &&
      attachment?.contentUrl;

    if (isFileDownloadInfo || isInlineImage) {

      //Convertimos el archivo descargándolo y pasándolo a base64.
      const converted = await convertTeamsAttachmentToDataUri(attachment, httpClient);

      // Validación de seguridad del archivo:
      const validation = validateFile({
        name: converted.name,
        size: converted.sizeBytes,
        type: converted.mimeType,
      } as File);

      // Si el archivo no es válido se lanza un error
      if (!validation.valid) {
        console.error(
          `Adjunto rechazado — contentType original de Teams: "${attachment?.contentType}", ` +
          `mimeType detectado: "${converted.mimeType}", nombre: "${converted.name}"`
        );
        throw new Error(validation.error);
      }

      // Si todo está correcto se agrega al resultado final
      results.push(converted);
    }
  }

  return results;
}

/**
 * Cliente HTTP mínimo compatible con `api.http` (@microsoft/teams.api), que ya
 * inyecta el token del bot como Authorization: Bearer en cada request.
 */
export interface AuthenticatedHttpClient {
  get: (url: string, config?: { responseType?: "arraybuffer" }) => Promise<{ data: ArrayBuffer | Buffer }>;
}

/**
 * Detecta el formato real de una imagen inspeccionando sus primeros bytes (magic numbers).
 * Necesario porque Teams a veces manda un contentType comodín ("image/*") para capturas
 * de pantalla pegadas directamente en el chat, que no sirve para determinar el formato real,
 * y esas imágenes tampoco traen extensión en el nombre para poder inferirlo de ahí.
 */
function detectImageMimeFromBytes(buffer: Buffer): string | undefined {
  if (buffer.length >= 8 && buffer[0] === 0x89 && buffer[1] === 0x50 && buffer[2] === 0x4e && buffer[3] === 0x47) {
    return "image/png";
  }
  if (buffer.length >= 3 && buffer[0] === 0xff && buffer[1] === 0xd8 && buffer[2] === 0xff) {
    return "image/jpeg";
  }
  if (buffer.length >= 6 && ["GIF87a", "GIF89a"].includes(buffer.toString("ascii", 0, 6))) {
    return "image/gif";
  }
  if (buffer.length >= 12 && buffer.toString("ascii", 0, 4) === "RIFF" && buffer.toString("ascii", 8, 12) === "WEBP") {
    return "image/webp";
  }
  return undefined;
}

/**
 * Convert a Microsoft Teams attachment (with downloadUrl)
 * to base64 data URI.
 */
export async function convertTeamsAttachmentToDataUri(
  attachment: any,
  httpClient?: AuthenticatedHttpClient
): Promise<FileConversionResult> {

  // Obtenemos el link de descarga del archivo.
  // content.downloadUrl -> archivos subidos por el clip (SharePoint, URL SAS pre-autenticada).
  // contentUrl -> imágenes adjuntadas/pegadas directamente o tomadas con la cámara (inline).
  const isInlineImage = !attachment?.content?.downloadUrl && !!attachment?.contentUrl;
  const downloadUrl = attachment?.content?.downloadUrl || attachment?.contentUrl;

  // Si no existe el link, el attachment no es válido
  if (!downloadUrl) {
    throw new Error("Attachment does not contain downloadUrl");
  }

  let buffer: Buffer;

  if (isInlineImage && httpClient) {
    // El contentUrl de una imagen inline es un endpoint protegido de Teams: requiere
    // el token del bot (Authorization: Bearer) para descargarse, no acceso anónimo.
    // Un fetch() sin auth devuelve 401 acá, que es la causa del bug reportado.
    const response = await httpClient.get(downloadUrl, { responseType: "arraybuffer" });
    buffer = Buffer.from(response.data as ArrayBuffer);
  } else {
    // Descargamos el archivo desde SharePoint (URL SAS, acceso anónimo)
    const response = await fetch(downloadUrl);

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    buffer = Buffer.from(arrayBuffer);
  }

  //Convertimos el buffer a base64
  const base64 = buffer.toString("base64");
  // DEBUG: verificar tamaño real vs base64
  console.log("Original bytes:", buffer.length);
  console.log("Base64 length:", base64.length);
  console.log("Approx MB original:", (buffer.length / 1024 / 1024).toFixed(2));
  console.log("Approx MB base64:", (base64.length / 1024 / 1024).toFixed(2));
  /**
     * Obtenemos el nombre del archivo.
     * Las imágenes inline de Teams normalmente no traen "name".
     */
  const fileName = attachment.name || `image_${Date.now()}`;

  //Extraemos la extensión del archivo
  const extension = fileName.toLowerCase().match(/\.[^.]+$/)?.[0] || "";

  // Preferimos el contentType que ya manda Teams (confiable para imágenes inline,
  // que no traen extensión en el nombre). Si es genérico, no viene, es un comodín
  // (ej. "image/*" para capturas de pantalla pegadas), o es el wrapper de "archivo
  // descargable" de Teams (file.download.info NO es el MIME real del archivo — el
  // tipo real hay que sacarlo del nombre), caemos a detección por extensión.
  const isGenericContentType = !attachment?.contentType ||
    attachment.contentType === "application/octet-stream" ||
    attachment.contentType === "application/vnd.microsoft.teams.file.download.info" ||
    attachment.contentType.includes("*");
  let mimeType = !isGenericContentType
    ? attachment.contentType
    : EXTENSION_TO_MIME[extension] ||
      (extension === ".jpg" || extension === ".jpeg"
        ? "image/jpeg"
        : extension === ".png"
          ? "image/png"
          : extension === ".webp"
            ? "image/webp"
            : extension === ".gif"
              ? "image/gif"
              : "application/octet-stream");

  // Último recurso: ni el contentType ni la extensión dieron un tipo útil (típico de
  // capturas de pantalla pegadas: contentType "image/*" + nombre sin extensión) —
  // inspeccionamos los bytes del archivo para determinar el formato real.
  if (mimeType === "application/octet-stream") {
    mimeType = detectImageMimeFromBytes(buffer) || mimeType;
  }

  // Creamos el Data URI
  const dataUri = `data:${mimeType};base64,${base64}`;

  //Retornamos el archivo ya convertido
  return {
    name: fileName,
    dataUri,
    mimeType,
    sizeBytes: buffer.length,
  };
}
